import React, { useEffect, useMemo, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";
import moment from "moment";
import {
  mdiCalendarMonthOutline,
  mdiChevronLeft,
  mdiChevronRight,
  mdiCloudOutline,
  mdiCurrentAc,
  mdiFire,
  mdiFlash,
  mdiInformationOutline,
  mdiLightningBolt,
  mdiLightningBoltOutline,
  mdiPineTree,
  mdiSineWave,
  mdiTransmissionTower,
  mdiWhiteBalanceSunny,
} from "@mdi/js";
import Main from "../../components/layout/Main";
import MdiIcon from "../../components/device/app/MdiIcon";
import { useToggleTop } from "../../components/common/ToggleTop";
import { UserService } from "../../services/UserService";
import "../../assets/styles/app-energy.css";

// An energy meter reading over time, as the app's redesigned
// app/screens/EnergyMeterGraphScreen.js shows it: the metric and meter in the
// header, the period tabs and date row, a summary card (the period's figure
// and a few worked out from its readings), the chart, and the environmental
// impact or the max demand note. Wide screens put the summary beside the chart.
// Colours are the app's tokens, light and dark (app-energy.css, .hx-emg-app).

const PERIODS = ["Hour", "Day", "Month", "Year"];
const YEAR_WINDOW = 6;
// the Hour tab's step, in minutes (the API's period=5min)
const FIVE_MINUTES = 5;

const METRICS = {
  consumed: {
    key: "consumed_kwh",
    title: "Energy Consumed",
    legendTitle: "Energy Consumed",
    heroLabel: "Total consumed",
    chartTitle: "consumption",
    unit: "kWh",
    hasTotal: true,
    hasAverage: true,
    hasEnvironment: true,
    icon: mdiLightningBolt,
  },
  generated: {
    key: "generated_kwh",
    title: "Energy Generated",
    legendTitle: "Energy Generated",
    heroLabel: "Total generated",
    chartTitle: "generation",
    unit: "kWh",
    hasTotal: true,
    hasAverage: true,
    hasEnvironment: true,
    icon: mdiWhiteBalanceSunny,
  },
  voltage: {
    key: "avg_voltage",
    title: "Voltage",
    legendTitle: "Average Voltage",
    heroLabel: "Average voltage",
    chartTitle: "voltage",
    unit: "V",
    hasAverage: true,
    icon: mdiFlash,
  },
  current: {
    key: "avg_current",
    title: "Current",
    legendTitle: "Average Current",
    heroLabel: "Average current",
    chartTitle: "current",
    unit: "A",
    hasAverage: true,
    icon: mdiCurrentAc,
  },
  power: {
    key: "avg_power",
    title: "Power",
    legendTitle: "Average Power",
    heroLabel: "Average power",
    chartTitle: "power",
    unit: "kW",
    icon: mdiLightningBoltOutline,
  },
  power_factor: {
    key: "avg_power_factor",
    title: "Power Factor",
    legendTitle: "Average Power Factor",
    heroLabel: "Average power factor",
    chartTitle: "power factor",
    unit: "Pf",
    hasAverage: true,
    // a line in every period; a 0 is no reading; the axis zooms in on the
    // readings; points under 0.90 are ringed
    line: true,
    isRatio: true,
    emptyWhenZero: true,
    reference: { value: 0.9, label: "0.90" },
    icon: mdiSineWave,
  },
  max_demand: {
    key: "max_demand",
    title: "Max Demand",
    legendTitle: "Max Demand",
    heroLabel: "Max demand",
    chartTitle: "max demand",
    unit: "kVA",
    hasTotal: true,
    // a period with no reading is 0: left out of the figures
    emptyWhenZero: true,
    decimals: 1,
    icon: mdiTransmissionTower,
  },
};

// Energy adds up over a period; Max Demand is the highest in it; voltage,
// current, power and power factor average out.
const SUMMED = ["consumed_kwh", "generated_kwh"];
const HIGHEST = ["max_demand"];

const PER_WORD = { Hour: "per 5 min", Day: "per day", Month: "per month", Year: "per year" };
const PEAK_WORD = { Hour: "Peak 5 min", Day: "Peak day", Month: "Peak month", Year: "Peak year" };
const AVERAGE_WORD = { Hour: "5-min average", Day: "Daily average", Month: "Monthly average", Year: "Yearly average" };
const CHART_WORD = { Hour: "5-minute", Day: "Daily", Month: "Monthly", Year: "Yearly" };
const HIGHEST_WORD = { Hour: "Highest in the day", Day: "Highest in the month", Month: "Highest in the year", Year: "Highest in 6 years" };
const WHEN_FORMAT = { Hour: "HH:mm", Day: "D MMM", Month: "MMM YYYY", Year: "YYYY" };

// The from/to the app requests for each period.
const rangeFor = (period, date) => {
  const now = moment();
  if (period === "Hour") {
    const day = date.clone().utc();
    return {
      from: day.clone().startOf("day").toISOString(),
      to: day.isSame(now.clone().utc(), "day") ? now.toISOString() : day.clone().endOf("day").toISOString(),
    };
  }
  if (period === "Day") {
    return {
      from: date.clone().startOf("month").toISOString(),
      to: date.isSame(now, "month") ? now.toISOString() : date.clone().endOf("month").toISOString(),
    };
  }
  if (period === "Month") {
    return {
      from: date.clone().startOf("year").toISOString(),
      to: date.isSame(now, "year") ? now.toISOString() : date.clone().endOf("year").toISOString(),
    };
  }
  return {
    from: date.clone().subtract(5, "year").startOf("year").toISOString(),
    to: date.isSame(now, "year") ? now.toISOString() : date.clone().endOf("year").toISOString(),
  };
};

const parseEnergyValue = (value) => {
  if (value === null || value === undefined) return 0;
  const parsed = parseFloat(String(value).replace(/[^\d.-]/g, ""));
  return Number.isNaN(parsed) ? 0 : parsed;
};

const aggregateByKey = (series, keyFormat, metricKey) => {
  const groups = new Map();
  series.forEach((item) => {
    if (!item?.bucket_time) return;
    const key = moment(item.bucket_time).format(keyFormat);
    groups.set(key, [...(groups.get(key) || []), parseEnergyValue(item[metricKey])]);
  });
  const result = new Map();
  groups.forEach((values, key) => {
    const sum = values.reduce((total, value) => total + value, 0);
    if (HIGHEST.includes(metricKey)) result.set(key, Math.max(...values));
    else result.set(key, SUMMED.includes(metricKey) ? sum : sum / values.length);
  });
  return result;
};

const yearsToShow = (series, referenceYear) => {
  const dataYears = series.map((item) => item?.bucket_time && moment(item.bucket_time).year()).filter(Number.isFinite);
  const maxDataYear = dataYears.length ? Math.max(...dataYears) : referenceYear;
  const minDataYear = dataYears.length ? Math.min(...dataYears) : referenceYear;
  const end = Math.min(Math.max(referenceYear, maxDataYear), moment().year());
  const start = Math.min(end - (YEAR_WINDOW - 1), minDataYear);
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
};

// One bar per 5 minutes, day, month or year of the period, 0 where nothing
// was recorded. The Hour tab's bucket_time values are UTC, so that day is
// built in UTC.
const buildPeriodBuckets = (series, period, referenceDate, metricKey) => {
  const safe = Array.isArray(series) ? series : [];
  const now = moment();
  const bucket = (time, value) => ({ bucket_time: time.toISOString(), value: value || 0 });

  if (period === "Hour") {
    const dayStart = referenceDate.clone().utc().startOf("day");
    const steps = referenceDate.clone().utc().isSame(moment().utc(), "day")
      ? Math.floor(moment().utc().diff(dayStart, "minutes") / FIVE_MINUTES) + 1
      : (24 * 60) / FIVE_MINUTES;
    const stepKey = (time) => {
      const step = moment.utc(time);
      return step.minutes(step.minutes() - (step.minutes() % FIVE_MINUTES)).format("YYYY-MM-DD-HH-mm");
    };
    const values = new Map();
    safe.forEach((item) => {
      if (item?.bucket_time) values.set(stepKey(item.bucket_time), parseEnergyValue(item[metricKey]));
    });
    return Array.from({ length: steps }, (_, index) => {
      const time = dayStart.clone().add(index * FIVE_MINUTES, "minutes");
      return bucket(time, values.get(time.format("YYYY-MM-DD-HH-mm")));
    });
  }
  if (period === "Day") {
    const sums = aggregateByKey(safe, "YYYY-MM-DD", metricKey);
    const days = referenceDate.isSame(now, "month") ? now.date() : referenceDate.daysInMonth();
    const monthStart = referenceDate.clone().startOf("month");
    return Array.from({ length: days }, (_, index) => {
      const time = monthStart.clone().add(index, "days");
      return bucket(time, sums.get(time.format("YYYY-MM-DD")));
    });
  }
  if (period === "Month") {
    const year = referenceDate.year();
    const sums = aggregateByKey(safe, "YYYY-MM", metricKey);
    const months = year === now.year() ? now.month() + 1 : 12;
    return Array.from({ length: months }, (_, index) => {
      const time = moment({ year, month: index, day: 1 });
      return bucket(time, sums.get(time.format("YYYY-MM")));
    });
  }
  const sums = aggregateByKey(safe, "YYYY", metricKey);
  return yearsToShow(safe, referenceDate.year()).map((year) => {
    const time = moment({ year, month: 0, day: 1 });
    return bucket(time, sums.get(time.format("YYYY")));
  });
};

const isFuture = (bucketTime, period) => {
  const time = moment(bucketTime);
  const now = moment();
  if (period === "Hour") return time.isAfter(now);
  return time.isAfter(now, { Day: "day", Month: "month", Year: "year" }[period]);
};

const SMALL_BUCKET_THRESHOLD = 15;

// Every label when there are few bars, otherwise the even ones and the last.
const dynamicLabelIndexes = (count, numberAt) => {
  if (count <= 0) return [];
  if (count <= SMALL_BUCKET_THRESHOLD) return Array.from({ length: count }, (_, index) => index);
  const indexes = [];
  for (let index = 0; index < count; index += 1) {
    if (numberAt(index) % 2 === 0) indexes.push(index);
  }
  if (!indexes.includes(count - 1)) indexes.push(count - 1);
  return indexes;
};

const labelIndexesFor = (bars, period) => {
  // a label every 2 hours, on the hour
  if (period === "Hour") {
    return bars
      .map((bar, index) => {
        const time = moment(bar.bucket_time);
        return time.minutes() === 0 && time.hours() % 2 === 0 ? index : -1;
      })
      .filter((index) => index >= 0);
  }
  if (period === "Month") return bars.map((_, index) => index);
  if (period === "Year") return dynamicLabelIndexes(bars.length, (index) => moment(bars[index].bucket_time).year());
  const indexes = dynamicLabelIndexes(bars.length, (index) => index + 1);
  // An odd last day replaces the even day before it, so the two don't collide.
  if (bars.length > SMALL_BUCKET_THRESHOLD && bars.length % 2 !== 0) {
    return indexes.filter((index) => index !== bars.length - 2).concat(bars.length - 1);
  }
  return indexes;
};

const AXIS_FORMAT = { Hour: "HH", Day: "D", Month: "MMM", Year: "YYYY" };
const TOOLTIP_FORMAT = { Hour: "D MMM, HH:mm", Day: "D MMM YYYY", Month: "MMM YYYY", Year: "YYYY" };

const formatEnergyValue = (value, decimals = 2) =>
  (Number(value) || 0).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const formatEcoValue = (value) => {
  const number = Number(value) || 0;
  return Math.abs(number) >= 100
    ? number.toLocaleString("en-US", { maximumFractionDigits: 0 })
    : number.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatTreeValue = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "--";
  if (Math.abs(number) < 1) return number.toFixed(3);
  return number.toLocaleString("en-US", { maximumFractionDigits: 1 });
};

const formatAxisValue = (value) => (value >= 10 ? value.toFixed(0) : value.toFixed(1));

// A bar with rounded top corners.
const barPath = (x, y, width, height, radius) => {
  if (height <= 0 || width <= 0) return "";
  const r = Math.max(0, Math.min(radius, width / 2, height));
  return `M ${x},${y + height} L ${x},${y + r} Q ${x},${y} ${x + r},${y} L ${x + width - r},${y} Q ${x + width},${y} ${x + width},${y + r} L ${x + width},${y + height} Z`;
};

// The width of an element that may come and go (the chart box isn't there
// while the empty state shows), so it takes the element itself, from a
// callback ref.
const useWidth = (element) => {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!element) return undefined;
    const update = () => setWidth(element.clientWidth);
    update();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  return width;
};

// Bars, or a line over a soft area on the Hour tab and for power factor.
// Pointing at the chart picks the nearest bar or point: the others step back
// and a callout shows its time and value. Colours come from the page's CSS
// variables, so the chart follows the theme.
const EnergyChart = ({ width, series, metricKey, average, hasAverage, period, referenceDate, unit, decimals, lineMode, isRatio, reference, label }) => {
  const [selectedIndex, setSelectedIndex] = useState(null);

  useEffect(() => {
    setSelectedIndex(null);
  }, [series, period, metricKey, referenceDate]);

  const bars = useMemo(
    () =>
      buildPeriodBuckets(series, period, referenceDate, metricKey).map((item, index) => ({
        ...item,
        index,
        // power factor: a 0 is no reading
        missing: isRatio && !(item.value > 0),
        isFuture: isFuture(item.bucket_time, period),
      })),
    [series, period, referenceDate, metricKey, isRatio]
  );

  if (width <= 0) return null;

  const isLine = lineMode || period === "Hour";
  const readings = bars.filter((bar) => !bar.isFuture && !bar.missing);
  const values = readings.map((bar) => bar.value);
  const averageShown = (hasAverage ? average : 0) || 0;
  const maxValue = values.length ? Math.max(...values, averageShown) : averageShown;
  // power factor tops out at 1; the bottom is the nearest of 0.8, 0.6 ... 0
  // under the lowest reading, so the four steps stay round numbers
  const chartMin = isRatio ? [0.8, 0.6, 0.4, 0.2].find((floor) => floor <= Math.min(...values, 1) - 0.02) ?? 0 : 0;
  const chartMax = isRatio ? 1 : maxValue <= 1 ? 1 : Math.ceil(maxValue * 1.2 * 10) / 10;

  const height = width >= 560 ? 300 : 246;
  const left = 38;
  const right = 6;
  const top = 16;
  const bottom = 30;
  const graphWidth = width - left - right;
  const graphHeight = height - top - bottom;
  const barSpace = bars.length ? graphWidth / bars.length : graphWidth;
  const barWidth = bars.length === 1 ? 34 : Math.max(isLine ? 3 : 5, Math.min(isLine ? 8 : 18, barSpace * 0.62));
  const dotRadius = Math.max(3, Math.min(5.5, barSpace * 0.22));
  const getY = (value) => graphHeight - ((value - chartMin) / (chartMax - chartMin)) * graphHeight;
  const getX = (index) => index * barSpace + barSpace / 2 - barWidth / 2;
  const getLineX = (index) => (bars.length > 1 ? (index / (bars.length - 1)) * graphWidth : graphWidth / 2);
  const centerOf = (index) => (isLine ? getLineX(index) : getX(index) + barWidth / 2);

  const linePoints = isLine ? readings.map((bar) => ({ x: getLineX(bar.index), y: getY(bar.value) })) : [];
  const linePath = linePoints.map((point, index) => `${index ? "L" : "M"} ${point.x},${point.y}`).join(" ");
  const areaPath =
    linePoints.length > 1
      ? `${linePath} L ${linePoints[linePoints.length - 1].x},${graphHeight} L ${linePoints[0].x},${graphHeight} Z`
      : "";

  const ySteps = 4;
  const labelIndexes = labelIndexesFor(bars, period);
  const lastPast = bars.reduce((last, bar, index) => (bar.isFuture ? last : index), -1);

  // the bar or point nearest the pointer; past the latest step the line parks
  // on it, and periods to come or without a reading pick nothing
  const pick = (event) => {
    if (lastPast < 0) return null;
    const box = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - box.left;
    const raw = isLine ? Math.round(bars.length > 1 ? (x / graphWidth) * (bars.length - 1) : 0) : Math.floor(x / barSpace);
    const index = Math.max(0, Math.min(bars.length - 1, raw));
    if (bars[index]?.isFuture) return isLine ? lastPast : null;
    return bars[index]?.missing ? null : index;
  };

  let tooltip = null;
  const active = selectedIndex !== null ? bars[selectedIndex] : null;
  if (active && !active.isFuture && !active.missing) {
    const y = getY(active.value);
    const centerX = centerOf(selectedIndex);
    const text = `${formatEnergyValue(active.value, decimals)} ${unit}`;
    const dateText = moment(active.bucket_time).format(TOOLTIP_FORMAT[period]);
    const bubbleWidth = Math.max(80, Math.max(text.length * 7.6, dateText.length * 5.8) + 24);
    const bubbleHeight = 40;
    const bubbleX = Math.max(0, Math.min(centerX - bubbleWidth / 2, graphWidth - bubbleWidth));
    // just above the bar or point, or at the top when that doesn't leave room
    const bubbleY = Math.max(y - 54, -top + 2);
    tooltip = (
      <g pointerEvents="none">
        <rect className="hx-emg-tip" x={bubbleX} y={bubbleY} width={bubbleWidth} height={bubbleHeight} rx={9} />
        <text className="hx-emg-tip-sub" x={bubbleX + bubbleWidth / 2} y={bubbleY + 15} fontSize={10} fontWeight="500" textAnchor="middle">
          {dateText}
        </text>
        <text className="hx-emg-tip-value" x={bubbleX + bubbleWidth / 2} y={bubbleY + 31} fontSize={13} fontWeight="600" textAnchor="middle">
          {text}
        </text>
        <polygon
          className="hx-emg-tip"
          points={`${centerX - 5},${bubbleY + bubbleHeight} ${centerX + 5},${bubbleY + bubbleHeight} ${centerX},${bubbleY + bubbleHeight + 5}`}
        />
      </g>
    );
  }

  const referenceY = reference && reference.value > chartMin && reference.value < chartMax ? getY(reference.value) : null;
  const chipWidth = reference ? 14 + 6 * reference.label.length : 0;

  return (
    <svg width={width} height={height} role="img" aria-label={label}>
      <defs>
        <linearGradient id="hx-emg-area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" className="hx-emg-area-top" />
          <stop offset="1" className="hx-emg-area-bottom" />
        </linearGradient>
      </defs>

      <g transform={`translate(${left},${top})`}>
        {Array.from({ length: ySteps + 1 }, (_, index) => {
          const ratio = index / ySteps;
          const value = chartMax - (chartMax - chartMin) * ratio;
          const y = graphHeight * ratio;
          return (
            <g key={`grid-${index}`}>
              <line className="hx-emg-grid" x1={0} y1={y} x2={graphWidth} y2={y} />
              <text className="hx-emg-axis" x={-7} y={y + 3.5} fontSize={10.5} fontWeight="500" textAnchor="end">
                {isRatio ? value.toFixed(2) : formatAxisValue(value)}
              </text>
            </g>
          );
        })}

        {hasAverage && average > 0 && average <= chartMax ? (
          <line className="hx-emg-average" x1={0} y1={getY(average)} x2={graphWidth} y2={getY(average)} />
        ) : null}

        {referenceY !== null ? (
          <g>
            <line className="hx-emg-reference" x1={0} y1={referenceY} x2={graphWidth} y2={referenceY} />
            <rect className="hx-emg-reference-chip" x={graphWidth - chipWidth} y={referenceY + 4} width={chipWidth} height={17} rx={8.5} />
            <text
              className="hx-emg-reference-text"
              x={graphWidth - chipWidth / 2}
              y={referenceY + 16}
              fontSize={10}
              fontWeight="600"
              textAnchor="middle"
            >
              {reference.label}
            </text>
          </g>
        ) : null}

        {isLine && areaPath ? <path d={areaPath} fill="url(#hx-emg-area)" /> : null}
        {isLine && linePath ? <path className="hx-emg-line" d={linePath} fill="none" /> : null}

        {bars.map((bar, index) => {
          const x = getX(index);
          const y = getY(bar.value);
          const barHeight = Math.max(graphHeight - y, bar.value > 0 ? 3 : 0);
          const isActive = index === selectedIndex;
          const centerX = centerOf(index);
          const below = isLine && reference && !bar.isFuture && !bar.missing && bar.value < reference.value;
          let mark = null;
          if (isLine) {
            if (isActive && !bar.isFuture && !bar.missing) {
              mark = (
                <>
                  <line className="hx-emg-guide" x1={centerX} y1={0} x2={centerX} y2={graphHeight} />
                  <circle className="hx-emg-point" cx={centerX} cy={y} r={Math.max(dotRadius, 4.5)} />
                </>
              );
            } else if (below) {
              mark = <circle className="hx-emg-point is-below" cx={centerX} cy={y} r={3.6} />;
            }
          } else if (bar.isFuture) {
            // a period still to come: a faint stub on the baseline
            mark = <rect className="hx-emg-stub" x={x} y={graphHeight - 2} width={barWidth} height={2} rx={1} />;
          } else {
            mark = (
              <path
                className={`hx-emg-bar ${selectedIndex !== null && !isActive ? "is-dim" : ""}`}
                d={barPath(x, graphHeight - barHeight, barWidth, barHeight, barWidth / 2.2)}
              />
            );
          }
          return (
            <g key={bar.bucket_time} pointerEvents="none">
              {mark}
              {labelIndexes.includes(index) ? (
                <text
                  className={`hx-emg-axis ${bar.isFuture ? "is-future" : isActive ? "is-active" : ""}`}
                  x={centerX}
                  y={graphHeight + 19}
                  fontSize={period === "Hour" || period === "Month" ? 9.5 : 10.5}
                  fontWeight={isActive ? "600" : "500"}
                  textAnchor="middle"
                >
                  {moment(bar.bucket_time).format(AXIS_FORMAT[period])}
                </text>
              ) : null}
            </g>
          );
        })}

        {tooltip}

        <rect
          x={0}
          y={-top}
          width={graphWidth}
          height={graphHeight + top}
          fill="transparent"
          onMouseMove={(event) => {
            const index = pick(event);
            if (index !== null) setSelectedIndex(index);
          }}
          onMouseLeave={() => setSelectedIndex(null)}
          onClick={(event) => {
            const index = pick(event);
            setSelectedIndex((current) => (index === null || current === index ? null : index));
          }}
        />
      </g>
    </svg>
  );
};

// One of the environmental impact figures: a tinted icon chip, the value, and
// what it is.
const EnvironmentItem = ({ icon, value, label, kind }) => (
  <div className="hx-emg-eco-item">
    <span className={`hx-emg-eco-icon is-${kind}`} aria-hidden="true">
      <MdiIcon path={icon} size={19} />
    </span>
    <span className="hx-emg-eco-value">{value}</span>
    <span className="hx-emg-eco-label">{label}</span>
  </div>
);

function EnergyMeterGraph() {
  const location = useLocation();
  const toast = useToggleTop();
  const device = location.state?.data;
  const metric = METRICS[location.state?.metric] ? location.state.metric : "consumed";
  const config = METRICS[metric];
  const decimals = config.decimals ?? 2;
  const deviceId = device?.deviceId;
  const [period, setPeriod] = useState("Day");
  const [selectedDate, setSelectedDate] = useState(() => moment());
  const [summary, setSummary] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chartBox, setChartBox] = useState(null);
  const chartWidth = useWidth(chartBox);

  useEffect(() => {
    if (!deviceId) return undefined;
    let current = true;
    const { from, to } = rangeFor(period, selectedDate);
    setLoading(true);
    // the Hour tab is a day in 5-minute steps
    UserService.getEnergyGraph(deviceId, period === "Hour" ? "5min" : period.toLowerCase(), from, to)
      .then((response) => {
        if (!current) return;
        setSummary(response?.summary || null);
        setChartData(response?.chart_data || null);
      })
      .catch(() => {
        if (!current) return;
        setSummary(null);
        setChartData(null);
        toast("Couldn't load the graph. Please try again.", "red");
      })
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
    };
    // toast is a new function on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, period, selectedDate]);

  const total = useMemo(() => {
    if (!config.hasTotal) return null;
    const key = {
      consumed: "total_energy_consumed_kwh",
      generated: "total_energy_generated_kwh",
      // the highest demand in the period
      max_demand: "max_demand_kva",
    }[metric];
    return Number(summary?.[key] ?? 0);
  }, [config, metric, summary]);

  // Energy's average comes under a key for the period it was asked for
  // (5min on the Hour tab); voltage, current and power factor have theirs in
  // the summary, else it's worked out from the chart.
  const average = useMemo(() => {
    if (!config.hasAverage) return null;
    if (metric === "consumed" || metric === "generated") {
      const prefix = metric === "generated" ? "avg_generation_per" : "avg_usage_per";
      const value = ["5min", "hour", "day", "month", "year"]
        .map((unit) => summary?.[`${prefix}_${unit}_kwh`])
        .find((item) => item !== undefined && item !== null);
      return value !== undefined ? Number(value) : 0;
    }
    const fromSummary = { voltage: summary?.avg_voltage, current: summary?.avg_current, power_factor: summary?.avg_power_factor }[metric];
    if (fromSummary !== undefined && fromSummary !== null) return Number(fromSummary);
    const values = (chartData?.series || []).map((item) => Number(item?.[config.key])).filter(Number.isFinite);
    return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
  }, [config, metric, summary, chartData]);

  // The period's readings as the chart draws them: periods still to come are
  // left out, and for power factor and max demand a 0 is no reading.
  const readings = useMemo(
    () =>
      buildPeriodBuckets(chartData?.series || [], period, selectedDate, config.key)
        .filter((item) => !isFuture(item.bucket_time, period))
        .map((item) => ({ time: item.bucket_time, value: item.value }))
        .filter((item) => !config.emptyWhenZero || item.value > 0),
    [chartData, period, selectedDate, config]
  );

  const extremes = useMemo(() => {
    if (!readings.length) return null;
    return {
      highest: readings.reduce((best, item) => (item.value > best.value ? item : best)),
      lowest: readings.reduce((best, item) => (item.value < best.value ? item : best)),
      mean: readings.reduce((sum, item) => sum + item.value, 0) / readings.length,
    };
  }, [readings]);

  const environment = useMemo(() => {
    if (!config.hasEnvironment) return null;
    if (metric === "generated") {
      const savings = summary?.eco_impact?.generation_savings;
      return {
        co2: Number(savings?.total_co2_reduced_kg ?? 0),
        coal: Number(savings?.total_coal_saved_kg ?? 0),
        trees: savings?.total_trees_saved !== undefined ? Number(savings.total_trees_saved) : null,
      };
    }
    const footprint = summary?.eco_impact?.consumption_footprint;
    return {
      co2: Number(footprint?.total_co2_produced_kg ?? 0),
      coal: Number(footprint?.total_coal_burned_kg ?? 0),
      trees: footprint?.total_trees_destroyed !== undefined ? Number(footprint.total_trees_destroyed) : null,
    };
  }, [config, metric, summary]);

  if (!device) return <Redirect to="/dashboard" />;

  const now = moment();
  const nextDisabled =
    period === "Hour"
      ? selectedDate.isSame(now, "day")
      : period === "Day"
      ? selectedDate.isSame(now, "month")
      : period === "Month"
      ? selectedDate.isSame(now, "year")
      : true;
  const dateLabel = period === "Hour" ? selectedDate.format("DD MMM YYYY") : period === "Day" ? selectedDate.format("MMMM YYYY") : selectedDate.format("YYYY");
  const step = { Hour: "day", Day: "month", Month: "year" }[period];

  const changeDate = (direction) => {
    if (direction > 0 && nextDisabled) return;
    if (step) setSelectedDate(selectedDate.clone().add(direction, step));
  };

  const changePeriod = (value) => {
    setPeriod(value);
    setSelectedDate(moment());
  };

  const generated = metric === "generated";
  const reference = config.reference || null;
  const noReadings = !loading && (!chartData?.series?.length || (config.emptyWhenZero && !readings.length));
  const when = (time) => (time ? moment(time).format(WHEN_FORMAT[period]) : "");
  const format = (value) => formatEnergyValue(value, decimals);
  const heroLabel = metric === "max_demand" ? HIGHEST_WORD[period] : config.heroLabel;
  const heroValue = total ?? average ?? extremes?.mean ?? 0;

  // The figures under the period's own: { label, value, unit, note }
  let stats = [];
  if (extremes) {
    const { unit } = config;
    if (metric === "consumed" || metric === "generated") {
      stats = [
        { label: AVERAGE_WORD[period], value: format(average), unit, note: PER_WORD[period] },
        { label: PEAK_WORD[period], value: format(extremes.highest.value), unit, note: when(extremes.highest.time) },
      ];
    } else if (metric === "max_demand") {
      // the API may name the 15-minute block of the highest demand
      // (max_demand_at); until it does, the period it fell in is shown
      const recorded = summary?.max_demand_at ? moment(summary.max_demand_at) : null;
      stats = [
        {
          label: "Recorded",
          value: recorded ? recorded.format("D MMM") : when(extremes.highest.time),
          note: recorded ? recorded.format("h:mm A") : "",
        },
        { label: AVERAGE_WORD[period], value: format(extremes.mean), unit },
      ];
    } else {
      stats = [
        { label: "Lowest", value: format(extremes.lowest.value), unit, note: when(extremes.lowest.time) },
        { label: "Highest", value: format(extremes.highest.value), unit, note: when(extremes.highest.time) },
      ];
      if (reference) {
        const below = readings.filter((item) => item.value < reference.value).length;
        stats.push({ label: `Below ${reference.label}`, value: String(below), note: `of ${readings.length}` });
      }
    }
  }

  const title = (
    <span className="hx-emg-title">
      <span className="hx-emg-title-main">{config.title}</span>
      {device.name ? <span className="hx-emg-title-sub">{device.name}</span> : null}
    </span>
  );

  return (
    <Main header="stack" title={title} className="hx-emg-app">
      <div className="hx-emg-page">
        <div className="hx-emg-top">
          <div className="hx-emg-periods" role="tablist" aria-label="Period">
            {PERIODS.map((item) => (
              <button
                key={item}
                type="button"
                role="tab"
                aria-selected={period === item}
                className={`hx-emg-period ${period === item ? "is-active" : ""}`}
                onClick={() => changePeriod(item)}
              >
                {item}
              </button>
            ))}
          </div>

          {period !== "Year" ? (
            <div className="hx-emg-datenav">
              <button type="button" className="hx-emg-arrow" aria-label={`Previous ${step}`} onClick={() => changeDate(-1)}>
                <MdiIcon path={mdiChevronLeft} size={20} />
              </button>
              <span className="hx-emg-date">
                <MdiIcon path={mdiCalendarMonthOutline} size={18} />
                {dateLabel}
              </span>
              <button type="button" className="hx-emg-arrow" aria-label={`Next ${step}`} disabled={nextDisabled} onClick={() => changeDate(1)}>
                <MdiIcon path={mdiChevronRight} size={20} />
              </button>
            </div>
          ) : null}
        </div>

        {noReadings ? (
          <div className="hx-emg-card hx-emg-empty">
            <span className="hx-emg-empty-ring" aria-hidden="true">
              <MdiIcon path={config.icon} size={36} />
            </span>
            <p className="hx-emg-empty-title">{`No ${config.title.toLowerCase()} data yet`}</p>
            <p className="hx-emg-empty-body">Readings show up here as your meter reports them. Check back soon.</p>
          </div>
        ) : (
          <div className="hx-emg-body">
            <section className="hx-emg-card hx-emg-summary" aria-label="Summary">
              <div className="hx-emg-hero">
                <div>
                  <div className="hx-emg-hero-label">{heroLabel}</div>
                  <div className="hx-emg-hero-value">
                    {format(heroValue)}
                    <small> {config.unit}</small>
                  </div>
                </div>
                <span className="hx-emg-chip" aria-hidden="true">
                  <MdiIcon path={config.icon} size={23} />
                </span>
              </div>
              {stats.length ? (
                <div className="hx-emg-stats">
                  {stats.map((stat) => (
                    <div key={stat.label} className="hx-emg-stat">
                      <div className="hx-emg-stat-label">{stat.label}</div>
                      <div className="hx-emg-stat-value">
                        {stat.value}
                        {stat.unit ? <small> {stat.unit}</small> : null}
                      </div>
                      {stat.note ? <div className="hx-emg-stat-note">{stat.note}</div> : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </section>

            <section className="hx-emg-card hx-emg-chart-card" aria-label="Chart">
              <div className="hx-emg-chart-head">
                <h2>{`${CHART_WORD[period]} ${config.chartTitle}`}</h2>
                <span className="hx-emg-unit">{config.unit}</span>
              </div>
              <div className="hx-emg-chart" ref={setChartBox}>
                {loading ? (
                  <div className="hx-emg-loading">
                    <span className="hx-em-spinner" aria-label="Loading" />
                  </div>
                ) : (
                  <EnergyChart
                    width={chartWidth}
                    series={chartData?.series}
                    metricKey={config.key}
                    average={average}
                    // power factor's average is in the figures above; a dashed
                    // line over its line chart only clutters it
                    hasAverage={Boolean(config.hasAverage) && !config.isRatio}
                    period={period}
                    referenceDate={selectedDate}
                    unit={config.unit}
                    decimals={decimals}
                    lineMode={Boolean(config.line)}
                    isRatio={Boolean(config.isRatio)}
                    reference={reference}
                    label={`${CHART_WORD[period]} ${config.chartTitle}, ${dateLabel}`}
                  />
                )}
              </div>
              <div className="hx-emg-legend">
                <span className="hx-emg-key">
                  <span className="hx-emg-key-swatch" />
                  {config.legendTitle}
                </span>
                {config.hasAverage && !config.isRatio ? (
                  <span className="hx-emg-key">
                    <span className="hx-emg-key-dash" />
                    Average
                  </span>
                ) : null}
                {reference ? (
                  <span className="hx-emg-key">
                    <span className="hx-emg-key-ring" />
                    {`Below ${reference.label}`}
                  </span>
                ) : null}
              </div>
            </section>

            {environment ? (
              <section className="hx-emg-side" aria-label="Environmental impact">
                <div className="hx-emg-section">
                  <h2>Environmental impact</h2>
                  <span>{dateLabel}</span>
                </div>
                <div className="hx-emg-card hx-emg-eco">
                  <EnvironmentItem
                    icon={mdiCloudOutline}
                    kind="cloud"
                    value={`${formatEcoValue(environment.co2)} kg`}
                    label={generated ? "CO₂ reduced" : "CO₂ produced"}
                  />
                  <span className="hx-emg-eco-divider" />
                  <EnvironmentItem
                    icon={mdiFire}
                    kind="coal"
                    value={`${formatEcoValue(environment.coal)} kg`}
                    label={generated ? "Coal saved" : "Coal burned"}
                  />
                  <span className="hx-emg-eco-divider" />
                  <EnvironmentItem
                    icon={mdiPineTree}
                    kind="tree"
                    value={environment.trees !== null ? `${formatTreeValue(environment.trees)} Trees` : "--"}
                    label={generated ? "Trees saved" : "Trees affected"}
                  />
                </div>
              </section>
            ) : null}

            {metric === "max_demand" ? (
              <p className="hx-emg-card hx-emg-side hx-emg-info">
                <MdiIcon path={mdiInformationOutline} size={17} />
                Max demand is the highest average load over any 15-minute block in the period.
              </p>
            ) : null}
          </div>
        )}
      </div>
    </Main>
  );
}

export default EnergyMeterGraph;
